import api from './api';

export interface Organization {
  id: number;
  name: string;
  created_by: number;
  created_by_name: string;
  members: OrganizationMembership[];
  created_at: string;
}

export interface OrganizationMembership {
  id: number;
  user: number;
  user_name: string;
  username: string;
  role: 'admin' | 'member';
  can_invite: boolean;
  joined_at: string;
}

export interface OrganizationInvitation {
  id: number;
  organization: number;
  organization_name: string;
  invited_by: number;
  invited_by_name: string;
  invited_user: number;
  invited_user_name: string;
  status: 'pending' | 'accepted' | 'declined';
  created_at: string;
}

export interface OrganizationVisibility {
  id: number;
  organization: number;
  granting_user: number;
  viewing_user: number;
  viewing_user_name: string;
  viewing_username: string;
  can_see_invoices: boolean;
  can_see_timeslots: boolean;
}

class OrganizationService {
  async getMyOrganizations(): Promise<Organization[]> {
    const response = await api.get('/organizations/');
    return Array.isArray(response.data) ? response.data : response.data.results || [];
  }

  async createOrganization(name: string): Promise<Organization> {
    const response = await api.post('/organizations/', { name });
    return response.data;
  }

  async getOrganization(id: number): Promise<Organization> {
    const response = await api.get(`/organizations/${id}/`);
    return response.data;
  }

  async renameOrganization(id: number, name: string): Promise<Organization> {
    const response = await api.patch(`/organizations/${id}/`, { name });
    return response.data;
  }

  async deleteOrganization(id: number): Promise<void> {
    await api.delete(`/organizations/${id}/`);
  }

  async inviteMember(orgId: number, userId: number): Promise<OrganizationInvitation> {
    const response = await api.post(`/organizations/${orgId}/invite/`, { user_id: userId });
    return response.data;
  }

  async removeMember(orgId: number, userId: number): Promise<void> {
    await api.post(`/organizations/${orgId}/remove_member/`, { user_id: userId });
  }

  async leaveOrganization(orgId: number): Promise<void> {
    await api.post(`/organizations/${orgId}/leave/`);
  }

  async updateMemberRights(orgId: number, userId: number, data: { can_invite?: boolean }): Promise<OrganizationMembership> {
    const response = await api.patch(`/organizations/${orgId}/update_member/`, { user_id: userId, ...data });
    return response.data;
  }

  async getPendingInvitations(): Promise<OrganizationInvitation[]> {
    const response = await api.get('/org-invitations/');
    return Array.isArray(response.data) ? response.data : response.data.results || [];
  }

  async acceptInvitation(invitationId: number): Promise<void> {
    await api.post(`/org-invitations/${invitationId}/accept/`);
  }

  async declineInvitation(invitationId: number): Promise<void> {
    await api.post(`/org-invitations/${invitationId}/decline/`);
  }

  async getVisibilitySettings(orgId: number): Promise<OrganizationVisibility[]> {
    const response = await api.get('/org-visibility/', { params: { organization: orgId } });
    return Array.isArray(response.data) ? response.data : response.data.results || [];
  }

  async updateVisibility(permId: number, data: { can_see_invoices?: boolean; can_see_timeslots?: boolean }): Promise<OrganizationVisibility> {
    const response = await api.patch(`/org-visibility/${permId}/`, data);
    return response.data;
  }
}

const organizationService = new OrganizationService();
export default organizationService;
